<template>
  <b-overlay :show="loading" rounded="sm">
    <b-alert v-if="loadError" show variant="danger" class="p-1">
      {{ loadError }}
      <b-link :to="{ name: 'applications' }" class="ml-50">Back to applications</b-link>
    </b-alert>

    <template v-if="application">
      <!-- Identity summary -->
      <b-card no-body class="border mb-2">
        <b-card-body>
          <b-row>
            <b-col cols="12" md="8" class="d-flex align-items-center">
              <b-avatar :src="application.logo" :text="initials" size="48" variant="light-primary" class="mr-1" />
              <div>
                <h4 class="mb-25">{{ application.title }}</h4>
                <div class="text-muted small mb-50">{{ application.full_url }}</div>
                <b-badge pill :variant="application.active ? 'light-success' : 'light-secondary'" class="text-capitalize">
                  {{ application.active ? "active" : "inactive" }}
                </b-badge>
                <b-badge pill variant="light-secondary" class="ml-50 text-capitalize">{{ application.access_type }}</b-badge>
              </div>
            </b-col>
            <b-col cols="12" md="4" class="text-md-right mt-1 mt-md-0">
              <div class="small text-muted mb-50">
                Token last used:
                {{ application.last_used_at ? formatDateDistance(application.last_used_at) : "never or not recorded" }}
              </div>
              <b-button variant="outline-secondary" size="sm" :to="{ name: 'applications' }">
                <feather-icon icon="ArrowLeftIcon" class="mr-50" />
                Applications
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <b-card no-body class="border">
        <b-tabs v-model="tabIndex" card>
          <!-- Settings -->
          <b-tab title="Settings">
            <b-overlay :show="saving">
              <b-alert show variant="info" class="p-1">
                Changes here affect only the integration fields. The application's name, URL, status and webhook are not touched.
              </b-alert>

              <b-row>
                <b-col cols="12" lg="6">
                  <h6 class="mb-1">Partner calling PAC</h6>
                  <b-form-group label="Application ID" description="The client_id for OIDC/PKCE sign-in. Public, safe to share with the partner. Not an API client id (pac_live_…).">
                    <b-input-group>
                      <b-form-input :value="application._id" readonly />
                      <b-input-group-append>
                        <b-button variant="outline-primary" @click="copy(application._id)">Copy</b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>

                  <b-form-group label="Allowed IP addresses (optional)" description="One per line. Leave empty to accept calls from any address. Applies to calls made with the application token.">
                    <b-form-textarea v-model="form.allowed_ips" rows="3" :disabled="!canEdit" placeholder="203.0.113.5" />
                  </b-form-group>

                  <b-form-group label="Redirect URIs" description="One per line. Sign-in with OIDC/PKCE only returns to an address listed here, exactly as written.">
                    <b-form-textarea v-model="form.redirect_uris" rows="3" :disabled="!canEdit" placeholder="com.example.app://callback" />
                  </b-form-group>

                  <b-form-group label="Inbound secret: environment variable name" description="Holds the secret this application signs its events with.">
                    <b-form-input v-model="form.inbound_secret_ref" :disabled="!canEdit" placeholder="PARTNER_EVENT_SECRET" />
                  </b-form-group>

                  <b-form-checkbox v-model="form.trusted_login_proxy" :disabled="!canEdit" class="mb-1">
                    Trusted login proxy: may sign users in with an application token and email, without a password or MFA
                  </b-form-checkbox>
                  <small v-if="form.trusted_login_proxy" class="text-danger d-block mb-1">
                    This bypasses password and MFA for any active user. Switch on only for a server you control.
                  </small>
                </b-col>

                <b-col cols="12" lg="6">
                  <h6 class="mb-1">PAC calling the partner</h6>
                  <b-form-group label="API base URL">
                    <b-form-input v-model="form.api_base_url" :disabled="!canEdit" placeholder="https://files.example.org" />
                  </b-form-group>

                  <b-form-group label="Outbound client id">
                    <b-form-input v-model="form.outbound_client_id" :disabled="!canEdit" />
                  </b-form-group>

                  <b-form-group label="Outbound secret: environment variable name" description="Name of the server variable that holds the secret. The secret itself is never entered here.">
                    <b-form-input v-model="form.outbound_secret_ref" :disabled="!canEdit" placeholder="PARTNER_CLIENT_SECRET" />
                  </b-form-group>

                  <b-form-checkbox v-model="form.can_act_for_users" :disabled="!canEdit" class="mb-1">
                    PAC may call this application on behalf of signed-in users (needed for the Electronic File tab)
                  </b-form-checkbox>
                </b-col>
              </b-row>

              <div v-if="canEdit" class="d-flex justify-content-end">
                <b-button variant="primary" @click="save">Save integration settings</b-button>
              </div>
            </b-overlay>
          </b-tab>

          <!-- Credentials -->
          <b-tab title="Credentials">
            <b-row>
              <b-col cols="12" lg="6" class="mb-2">
                <h6>Application access token</h6>
                <p class="text-muted">
                  Last used: {{ application.last_used_at ? formatDate(application.last_used_at) : "never or not recorded" }}.
                  New tokens last one year.
                </p>
                <b-form-checkbox v-model="rotate" class="mb-1">
                  Keep the current token working for 24 hours (recommended: lets the partner switch without downtime)
                </b-form-checkbox>
                <b-button v-if="canEdit" variant="outline-primary" @click="generateToken">
                  {{ rotate ? "Rotate access token" : "Replace access token (old one stops now)" }}
                </b-button>
                <div v-if="newToken" class="mt-1">
                  <b-alert show variant="warning" class="p-1">Copy this now. It cannot be shown again.</b-alert>
                  <b-form-textarea :value="newToken" rows="4" readonly />
                  <b-button class="mt-1" @click="copy(newToken)">Copy</b-button>
                </div>
              </b-col>

              <b-col cols="12" lg="6" class="mb-2">
                <h6>Webhook signing secret</h6>
                <p class="text-muted">
                  Deliveries carry an <code>X-PAC-Signature</code> header when a secret exists. A new secret replaces the old one immediately,
                  so give it to the receiving team first.
                </p>
                <b-button v-if="canEdit" variant="outline-primary" @click="rotateSecret">Generate new webhook secret</b-button>
                <div v-if="newSecret" class="mt-1">
                  <b-alert show variant="warning" class="p-1">Copy this now. It cannot be shown again.</b-alert>
                  <b-input-group>
                    <b-form-input :value="newSecret" readonly />
                    <b-input-group-append>
                      <b-button @click="copy(newSecret)">Copy</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </b-col>
            </b-row>
          </b-tab>

          <!-- Sign-in guide -->
          <b-tab title="Sign-in guide" lazy>
            <application-sign-in-guide
              :application="application"
              @blocked="toast('Pop-up blocked', 'danger', 'Allow pop-ups for PAC, then press Export PDF again.')"
            />
          </b-tab>

          <!-- API usage -->
          <b-tab title="API usage">
            <b-overlay :show="loadingUsage">
              <div class="d-flex flex-wrap align-items-center mb-1">
                <b-form-select v-model="usageDays" :options="usageDayOptions" size="sm" class="mr-1 mb-50" style="width: auto" @change="loadUsage" />
                <b-form-checkbox v-model="usageFailuresOnly" class="mr-1 mb-50" @change="loadUsage">Failed calls only</b-form-checkbox>
                <b-button size="sm" variant="outline-secondary" class="ml-auto mb-50" @click="loadUsage">Refresh</b-button>
              </div>

              <template v-if="usage">
                <b-alert show :variant="usageStatus.variant" class="p-1">{{ usageStatus.text }}</b-alert>

                <b-row class="mb-1">
                  <b-col v-for="tile in usageTiles" :key="tile.label" cols="6" lg="3" class="mb-1">
                    <div class="border rounded p-1 text-center h-100">
                      <h4 class="mb-0" :class="tile.class">{{ tile.value }}</h4>
                      <small class="text-muted">{{ tile.label }}</small>
                    </div>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="12" xl="8">
                    <h6>Endpoints called</h6>
                    <b-table small responsive show-empty empty-text="No calls in this period." :items="usage.routes" :fields="usageRouteFields">
                      <template #cell(route)="data"><code>{{ data.item.method }} {{ data.value }}</code></template>
                      <template #cell(errors)="data">
                        <span :class="{ 'text-danger': data.value }">{{ data.value }}</span>
                      </template>
                      <template #cell(last_at)="data">{{ formatDate(data.value) }}</template>
                    </b-table>
                  </b-col>
                  <b-col cols="12" xl="4">
                    <h6>Calls per day</h6>
                    <b-table small responsive show-empty empty-text="No calls in this period." :items="usage.by_day" :fields="usageDayFields">
                      <template #cell(calls)="data">
                        <div class="d-flex align-items-center">
                          <div class="usage-bar mr-50" :style="{ width: `${barWidth(data.value)}%` }" />
                          {{ data.value }}
                        </div>
                      </template>
                      <template #cell(errors)="data">
                        <span :class="{ 'text-danger': data.value }">{{ data.value }}</span>
                      </template>
                    </b-table>
                  </b-col>
                </b-row>

                <h6>{{ usageFailuresOnly ? "Latest failed calls" : "Latest calls" }}</h6>
                <b-table small responsive show-empty empty-text="No calls recorded." :items="usage.recent" :fields="usageRecentFields">
                  <template #cell(created_at)="data">{{ formatDate(data.value) }}</template>
                  <template #cell(route)="data"><code>{{ data.item.method }} {{ data.value }}</code></template>
                  <template #cell(status_code)="data">
                    <b-badge :variant="data.value >= 400 ? 'light-danger' : 'light-success'">{{ data.value }}</b-badge>
                  </template>
                </b-table>
              </template>
            </b-overlay>
          </b-tab>

          <!-- Deliveries -->
          <b-tab title="Webhook deliveries">
            <b-overlay :show="loadingDeliveries">
              <div class="d-flex justify-content-between mb-1">
                <b-button size="sm" variant="outline-secondary" @click="loadDeliveries">Refresh</b-button>
                <b-button v-if="canEdit" size="sm" variant="outline-primary" :disabled="!application.webhook_url" @click="sendTest">
                  Send test event
                </b-button>
              </div>
              <b-alert v-if="!application.webhook_url" show variant="warning" class="p-1">
                This application has no webhook URL. Add one by editing the application.
              </b-alert>
              <b-table small responsive show-empty empty-text="No deliveries yet." :items="deliveries" :fields="deliveryFields">
                <template #cell(status)="data">
                  <b-badge :variant="statusVariant(data.value)">{{ data.value }}</b-badge>
                </template>
                <template #cell(created_at)="data">{{ formatDate(data.value) }}</template>
                <template #cell(next_attempt_at)="data">{{ data.value ? formatDate(data.value) : "" }}</template>
              </b-table>
            </b-overlay>
          </b-tab>
        </b-tabs>
      </b-card>
    </template>
  </b-overlay>
</template>

<script>
import { get } from "lodash";
import {
  BAlert, BAvatar, BBadge, BButton, BCard, BCardBody, BCol, BFormCheckbox, BFormGroup, BFormInput, BFormSelect,
  BFormTextarea, BInputGroup, BInputGroupAppend, BLink, BOverlay, BRow, BTab, BTable, BTabs,
} from "bootstrap-vue";
import { formatDateDistance } from "@core/utils/utils";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ApplicationSignInGuide from "@/@core/components/shared/church/ApplicationSignInGuide.vue";

const lines = (list) => (list || []).join("\n");

// URL names for the tabs, in display order, so a tab can be linked to (?tab=usage)
const TABS = ["settings", "credentials", "guide", "usage", "deliveries"];

// Integration settings for one application (file provider and other partners). Uses its own endpoints so
// that saving here can never change the application's name, status or webhook.
export default {
  components: {
    ApplicationSignInGuide,
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BOverlay,
    BRow,
    BTab,
    BTable,
    BTabs,
  },
  data() {
    return {
      application: null,
      loading: false,
      loadError: "",
      saving: false,
      form: {},
      newSecret: "",
      newToken: "",
      rotate: true,
      deliveries: [],
      deliveriesLoaded: false,
      loadingDeliveries: false,
      deliveryFields: [
        { key: "created_at", label: "When" },
        { key: "event", label: "Event" },
        { key: "status", label: "Status" },
        { key: "attempts", label: "Tries" },
        { key: "last_status_code", label: "HTTP" },
        { key: "next_attempt_at", label: "Next try" },
        { key: "last_error", label: "Last error" },
      ],
      usage: null,
      loadingUsage: false,
      usageDays: 7,
      usageFailuresOnly: false,
      usageDayOptions: [
        { value: 1, text: "Last 24 hours" },
        { value: 7, text: "Last 7 days" },
        { value: 30, text: "Last 30 days" },
      ],
      usageRouteFields: [
        { key: "route", label: "Endpoint" },
        { key: "calls", label: "Calls" },
        { key: "errors", label: "Failed" },
        { key: "avg_ms", label: "Avg ms" },
        { key: "last_at", label: "Last call" },
      ],
      usageDayFields: [
        { key: "date", label: "Day" },
        { key: "calls", label: "Calls" },
        { key: "errors", label: "Failed" },
      ],
      usageRecentFields: [
        { key: "created_at", label: "When" },
        { key: "route", label: "Endpoint" },
        { key: "status_code", label: "HTTP" },
        { key: "duration_ms", label: "ms" },
        { key: "ip", label: "IP" },
        { key: "user_agent", label: "Client" },
        { key: "error", label: "Error" },
      ],
    };
  },
  computed: {
    recordId() {
      return this.$route.params.id;
    },
    canEdit() {
      return this.can("applications:CanUpdate");
    },
    initials() {
      const words = (this.application?.title || "").split(/\s+/);
      return words.map((w) => w[0] || "").join("").slice(0, 2)
        .toUpperCase();
    },
    tabIndex: {
      get() {
        return Math.max(TABS.indexOf(this.$route.query.tab), 0);
      },
      set(index) {
        const tab = TABS[index] || TABS[0];
        if (tab !== this.$route.query.tab) this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
      },
    },
    // one line answering "is this application using the API, and is it working"
    usageStatus() {
      const { usage } = this;
      if (!usage.last_call_at) {
        return { variant: "warning", text: "No calls recorded. This application has not used its application token since logging started." };
      }
      const period = this.usageDayOptions.find((o) => o.value === usage.days)?.text.toLowerCase() || `last ${usage.days} days`;
      const last = `Last call ${formatDateDistance(usage.last_call_at)}.`;
      if (!usage.totals.calls) {
        return { variant: "warning", text: `${last} No calls in the ${period}.` };
      }
      if (usage.totals.errors === usage.totals.calls) {
        return { variant: "danger", text: `${last} Every call in the ${period} failed. Check the latest failed calls below.` };
      }
      return { variant: "success", text: `${last} In use: ${usage.totals.calls.toLocaleString()} calls in the ${period}.` };
    },
    usageTiles() {
      const { totals } = this.usage;
      return [
        { label: "Calls", value: totals.calls.toLocaleString() },
        { label: "Failed", value: totals.errors.toLocaleString(), class: { "text-danger": totals.errors } },
        { label: "Average response", value: `${totals.avg_ms} ms` },
        { label: "Last successful call", value: totals.last_success_at ? formatDateDistance(totals.last_success_at) : "none" },
      ];
    },
  },
  watch: {
    recordId: {
      immediate: true,
      handler() {
        this.loadApplication();
      },
    },
    // usage and deliveries load when their tab is first shown, including when opened from a link
    tabIndex: {
      immediate: true,
      handler() {
        this.loadTabData();
      },
    },
  },
  methods: {
    formatDateDistance,
    service() {
      return this.useJwt().mainService.applications;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "";
    },
    statusVariant(status) {
      return { delivered: "light-success", pending: "light-warning", failed: "light-danger" }[status] || "light-secondary";
    },
    barWidth(calls) {
      const max = Math.max(...this.usage.by_day.map((d) => d.calls), 1);
      return Math.max(Math.round((calls / max) * 70), 2);
    },
    toast(title, variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === "success" ? "CheckIcon" : "AlertTriangleIcon", variant, text },
      });
    },
    fail(error) {
      this.toast("Error!", "danger", get(error, "response.data.message") || error.message);
    },
    setApplication(app) {
      this.application = app;
      this.form = {
        allowed_ips: lines(app.allowed_ips),
        redirect_uris: lines(app.redirect_uris),
        api_base_url: app.api_base_url || "",
        outbound_client_id: app.outbound_client_id || "",
        outbound_secret_ref: app.outbound_secret_ref || "",
        inbound_secret_ref: app.inbound_secret_ref || "",
        can_act_for_users: !!app.can_act_for_users,
        trusted_login_proxy: !!app.trusted_login_proxy,
      };
    },
    async loadApplication() {
      this.loading = true;
      this.loadError = "";
      this.newSecret = "";
      this.newToken = "";
      this.usage = null;
      this.deliveries = [];
      this.deliveriesLoaded = false;
      try {
        const { data } = await this.service().get(this.recordId);
        this.setApplication(data.data);
        this.loadTabData();
      } catch (error) {
        this.application = null;
        this.loadError = get(error, "response.data.message") || error.message;
      } finally {
        this.loading = false;
      }
    },
    loadTabData() {
      if (!this.application) return;
      const tab = TABS[this.tabIndex];
      if (tab === "usage" && !this.usage && !this.loadingUsage) this.loadUsage();
      if (tab === "deliveries" && !this.deliveriesLoaded && !this.loadingDeliveries) this.loadDeliveries();
    },
    async save() {
      this.saving = true;
      try {
        const { data } = await this.service().updateIntegration(this.application._id, this.form);
        this.setApplication(data.data);
        this.toast("Saved", "success", "Integration settings updated.");
      } catch (error) {
        this.fail(error);
      } finally {
        this.saving = false;
      }
    },
    async rotateSecret() {
      const result = await this.$swal({
        title: "Generate a new webhook secret?",
        text: "The receiving application must be updated with it, or it will reject signed deliveries.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, generate",
        customClass: { confirmButton: "btn btn-primary", cancelButton: "btn btn-outline-danger ml-1" },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await this.service().rotateWebhookSecret(this.application._id);
        this.newSecret = get(data, "data.webhook_secret", "");
      } catch (error) {
        this.fail(error);
      }
    },
    async generateToken() {
      const result = await this.$swal({
        title: this.rotate ? "Rotate access token?" : "Replace access token?",
        text: this.rotate
          ? "A new token is issued. The current one keeps working for 24 hours."
          : "A new token is issued and the current one stops working immediately. The partner's integration will fail until it is updated.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, proceed",
        customClass: { confirmButton: "btn btn-primary", cancelButton: "btn btn-outline-danger ml-1" },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await this.service().createAccessToken(this.application._id, { rotate: this.rotate });
        this.newToken = get(data, "data.token", "");
      } catch (error) {
        this.fail(error);
      }
    },
    async loadUsage() {
      this.loadingUsage = true;
      try {
        const { data } = await this.service().apiUsage(this.application._id, {
          days: this.usageDays,
          limit: 50,
          failures_only: this.usageFailuresOnly,
        });
        this.usage = data.data;
      } catch (error) {
        this.fail(error);
      } finally {
        this.loadingUsage = false;
      }
    },
    async loadDeliveries() {
      this.loadingDeliveries = true;
      try {
        const { data } = await this.service().webhookDeliveries(this.application._id, { limit: 50 });
        this.deliveries = data.data || [];
        this.deliveriesLoaded = true;
      } catch (error) {
        this.fail(error);
      } finally {
        this.loadingDeliveries = false;
      }
    },
    async sendTest() {
      try {
        await this.service().sendWebhookTest(this.application._id);
        this.toast("Queued", "success", "A test event is on its way. Refresh in a few seconds.");
        setTimeout(this.loadDeliveries, 3000);
      } catch (error) {
        this.fail(error);
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.toast("Copied", "success", "Copied to the clipboard.");
      } catch (error) {
        this.toast("Copy failed", "danger", "Select the text and copy it manually.");
      }
    },
  },
};
</script>

<style scoped>
.usage-bar {
  height: 8px;
  border-radius: 4px;
  background-color: var(--primary, #7367f0);
}
</style>
